import { useEffect, useRef, useState } from 'react';
import type { IScore } from '../../viewModels/gamification';
import { isLoadedSnapshot, type DialPacingSnapshot } from './types';

export type DialMilestone = {
	dialsToday: number;
	connectsToday: number;
	demosBookedToday: number;
	dialGoal: number | null;
	score?: IScore;
	goals?: IScore;
	projectedScore?: IScore;
	isSalesCoffee: boolean;
};

export type UseDialMilestoneTriggerResult = {
	open: boolean;
	milestone: DialMilestone | null;
	close: () => void;
};

export type UseDialMilestoneTriggerOptions = {
	/**
	 * When true, a crossed milestone is deferred instead of opening the dialog.
	 * The pacing poll refetches every 60s, so on any call longer than that the
	 * refetch can cross a bucket and pop the dialog over a live call — deferring
	 * holds it until the call ends.
	 */
	callIsActive?: boolean;
	interval?: number;
};

/**
 * Watches `snapshot.dialsToday` and opens a milestone dialog every time the
 * count crosses a multiple of `interval`. The first loaded value is treated
 * as the baseline (no fire on mount) so a rep with 12 dials already on the
 * board doesn't see a stale milestone the moment the view loads.
 */
export function useDialMilestoneTrigger(
	snapshot: DialPacingSnapshot,
	{ callIsActive = false, interval = 5 }: UseDialMilestoneTriggerOptions = {}
): UseDialMilestoneTriggerResult {
	const lastBucketRef = useRef<number | null>(null);
	const [open, setOpen] = useState(false);
	const [milestone, setMilestone] = useState<DialMilestone | null>(null);

	useEffect(() => {
		if (!isLoadedSnapshot(snapshot)) return;
		const bucket = Math.floor(snapshot.dialsToday / interval);
		if (lastBucketRef.current === null) {
			lastBucketRef.current = bucket;
			return;
		}
		// Hold the bucket while a call is active so the crossing re-detects and
		// fires once on hangup, rather than popping over the live call.
		if (callIsActive) return;
		if (bucket > lastBucketRef.current && snapshot.dialsToday > 0) {
			setMilestone({
				dialsToday: snapshot.dialsToday,
				connectsToday: snapshot.connectsToday,
				demosBookedToday: snapshot.demosBookedToday,
				dialGoal: snapshot.dialGoal,
				score: snapshot.score,
				goals: snapshot.goals,
				projectedScore: snapshot.projectedScore,
				isSalesCoffee: snapshot.isSalesCoffee,
			});
			setOpen(true);
		}
		lastBucketRef.current = bucket;
	}, [snapshot, interval, callIsActive]);

	return {
		open,
		milestone,
		close: () => setOpen(false),
	};
}
